// @flow

import React, { useEffect, useState } from "react";
import { Link } from 'react-router-dom';
import { history } from '../../../lib/history';
import { useEmailVerification } from '../../../hooks/useEmailVerification';
import { ApplicationList } from './edit/ApplicationList';
import { TwoFactorEnableDialog } from './edit/TwoFactorAuthentication';
import { TwoFactorBackupCodesDialog } from "./edit/TwoFactorBackupCodesDialog";
import { AccountList } from './AccountList';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { toast } from 'react-toastify';
import { useTabs } from '../../element/UiStateHooks';
import { Panel, PanelHeader, PanelMultiSettingsBar } from '../../element/Panel';
import { useResourceEditorModal } from '../../common/Editor';
import { ResourceAddViewRoute } from '../../element/ResourceAddViewRoute';
import { NameEditPanel } from '../../element/NameEditPanel';
import { RC_CACHED } from '../../../state/resource/type';
import { Dialog, useDialog } from '../../element/Dialog';
import { TextInput } from '../../element/Input';
import { useSelector } from 'react-redux';
import { useDeleteCollaboration, usePendingCollaborations } from '../../hoc/Collaboration';
import { UserSshKeyEditor } from './edit/UserSshKeyEditor';
import { SkeletonBar } from '../../element/Skeleton';
import { UserEmailEditor } from "./edit/UserEmailEditor";
import { HiddenPassword } from '../../element/HiddenPassword';
import { UserPasswordEditor } from './edit/UserPasswordEditor';
import { Avatar } from '../../element/Avatar';
import { initial } from '../../element/Chip';
import { useTwoFactorEditor, useViewUser, usePasswordEditor } from '../../hoc/User';
import { LABELS } from '../../element/ResourceLabels';
import { Pill } from '../../element/Pill';
import { Tooltip } from '../../element/Tooltip';
import { TooltipButton } from '../../element/Button';
import { isAbleToAcceptCollaboration } from '../../../lib/user-collaborations';
import { validateSshKeys } from "../../element/lib/sshKeys";

import type { Match } from 'react-router-dom';
import type { BbCollaboration, BbUser, BbUserPatchParams } from '../../../api/type';
import type { EditorDef } from '../../common/Editor';
import type { BbCollectedAccount } from '../../../api/type.acc';
import type { CloudGuiState } from '../../../state/cloudgui';

const hashMap = new Map < string, number> ([
    ['#settings', 0],
    ['#accounts', 1],
    ['#application', 2],
]);

const sshKeyDetail = (key: string) => {
    if (key !== '') {
        const [algo, pub, comment] = key.split(' ');
        return [algo, pub.substr(0, 8) + '...', comment || ''].join(' ');
    } else {
        return '';
    }
};

const nameDef: EditorDef<BbUser, string, BbUserPatchParams, null> = {
    emptyErrors: null,
    editUri: 'name',
    onEdit: (res: BbUser) => res.name || '',
    onValidate: null,
};
const emailDef: EditorDef<BbUser, string, BbUserPatchParams, null> = {
    emptyErrors: null,
    editUri: 'email_address',
    onEdit: (res: BbUser) => res.email_address,
    onValidate: null,
};
const ssh_keyDef: EditorDef<BbUser, string, BbUserPatchParams, null> = {
    emptyErrors: null,
    editUri: 'ssh_key',
    onEdit: (res: BbUser) => res.ssh_key,
    onValidate: (r, v) => {
        const res = validateSshKeys(v);
        if (res[1] != null) return [null, { ssh_key: res[1] }];
        return res;
    },
};


function useAccountProps() {
    const currUserId = useSelector < CloudGuiState, string> ((state) => state.Auth.currUser ? state.Auth.currUser.id : '');
    const currentUser = useSelector < CloudGuiState, BbUser | null > ((state) => state.Auth.currUser);
    const pendingCollaborations = usePendingCollaborations();
    const deleteCollab = useDeleteCollaboration();

    const leaveDialog = useDialog < BbCollectedAccount > ([
        {
            label: 'Leave Account',
            kind: 'primary',
            color: 'red',
            onSelect: (data: ?BbCollectedAccount) => {
                if (data) {
                    deleteCollab.deleteCollaboration(currUserId, data.id);
                }
                return false;
            }
        }
    ]);

    const confirmAcceptDialog = useDialog < BbCollaboration > ([
        {
            label: 'Accept Invitation',
            kind: 'primary',
            color: 'blue',
            onSelect: (collab) => {
                if (collab) {
                    return pendingCollaborations.accept(collab);
                }
                return false;
            },
            disabled: (collab) => {
                if (collab) {
                    return !isAbleToAcceptCollaboration(currentUser, collab);
                }
                return false;
            },
        },
    ]);

    const confirmDeclineDialog = useDialog < BbCollaboration > ([
        {
            label: 'Decline Invitation',
            kind: 'primary',
            color: 'red',
            onSelect: (collab) => {
                if (collab) {
                    return pendingCollaborations.reject(collab);
                }
                return false;
            },
        },
    ]);

    return {
        currUserId,
        pendingCollaborations, deleteCollab,
        leaveDialog,
        confirmAcceptDialog,
        confirmDeclineDialog,
    };

}

export const UserProfile = ({ id, match }: { id: string, match: Match }): React$Node => {
    const resource = useViewUser(id);
    const { item: user, status: cacheStatus, } = resource;
    const tabs = useTabs(hashMap);

    const name = useResourceEditorModal(resource, nameDef, match.path);
    const email = useResourceEditorModal(resource, emailDef, match.path, resource.emailMessages);
    const sshKey = useResourceEditorModal(resource, ssh_keyDef, match.path);
    const passwordEditor = usePasswordEditor();
    const twoFactorHook = useTwoFactorEditor(id);
    const {
        enableDialog: enableTwoFactorDialog,
        disableDialog: disableTwoFactorDialog,
        messages: twoFactorMessages,
    } = twoFactorHook;

    const [disableTwoFactorPassword, setDisableTwoFactorPassword] = useState<string>('');

    useEffect(() => {
        if (disableTwoFactorDialog.visible && typeof disableTwoFactorDialog.setData === 'function') {
            disableTwoFactorDialog.setData(disableTwoFactorPassword);
        }
    }, [disableTwoFactorPassword, disableTwoFactorDialog, disableTwoFactorDialog.visible, disableTwoFactorDialog.setData]);

    // Email verification is done via the Kiosk API for authenticated user
    const {
        isEmailVerificationRequestable,
        isUserEmailVerified,
        isVerificationLoading,
        isQueryLoading,
        pendingEmail,
        requestEmailVerification,
    } = useEmailVerification();

    const { currUserId, leaveDialog, confirmAcceptDialog, confirmDeclineDialog, pendingCollaborations, deleteCollab, } = useAccountProps();

    const twoFactorButton = user
        ? (
            user['2fa'].enabled
                ? {
                    onClick: () => {
                        setDisableTwoFactorPassword('');
                        disableTwoFactorDialog.show('');
                    },
                    children: 'Disable Two-Factor',
                    kind: 'secondary',
                    disabled: user['2fa'].required,
                }
                : {
                    onClick: () => enableTwoFactorDialog.show(),
                    children: 'Enable Two-Factor',
                    kind: 'secondary',
                }
        )
        : null;

    const handleRequestEmailVerification = async () => {
        try {
            const result = await requestEmailVerification();

            if (result && !result.error) {
                toast.success('Email verification requested successfully!');
            }
        } catch (error) {
            console.error('Error requesting email verification:', error);
            toast.error('Error requesting email verification');
        }
    }

    return (
        <ResourceAddViewRoute
            match={match}
            dialog={
                <>
                    <Dialog
                        title='Leave Account?'
                        dialog={leaveDialog}
                        messages={deleteCollab.messages}
                        render={(data: ?BbCollectedAccount) => <div>Are you sure want you to leave account {data?.id}?</div>}
                    />
                    <Dialog
                        title='Accept Invitation?'
                        dialog={confirmAcceptDialog}
                        messages={pendingCollaborations.messages}
                        render={(collab) =>
                            <>
                                <p>Please confirm you want to accept the invitation to {collab?.account?.name}.</p>
                            </>
                        }
                    />
                    <Dialog
                        title='Decline Invitation?'
                        dialog={confirmDeclineDialog}
                        messages={pendingCollaborations.messages}
                        render={(collab) =>
                            <>
                                <p>Please confirm you want to decline the invitation to {collab?.account?.name}?</p>
                            </>
                        }
                    />
                    <TwoFactorEnableDialog twoFactor={twoFactorHook} user={user} userLoading={cacheStatus !== RC_CACHED} />
                    <Dialog
                        title='Disable Two-Factor Authentication?'
                        dialog={disableTwoFactorDialog}
                        messages={twoFactorMessages}
                        render={(dialogData) =>
                            <div>
                                <p>Are you sure you want to disable Two-Factor authentication?</p>
                                <p>Any backup codes will become invalid.</p>
                                <TextInput
                                    type="password"
                                    placeholder="Password"
                                    value={disableTwoFactorPassword}
                                    onChange={setDisableTwoFactorPassword}
                                    className='mt-2'
                                    autoFocus
                                />
                            </div>
                        }
                        password={disableTwoFactorPassword}
                        setPassword={setDisableTwoFactorPassword}
                    />
                    <TwoFactorBackupCodesDialog userId={id} />
                </>
            }
            view={
                <Panel>
                    <PanelHeader
                        cacheStatus={resource.status}
                        title={user?.name || ''}
                        editTitleLink={name.editUri}
                    />
                    <Tabs {...tabs}>
                        <TabList>
                            <Tab>User Settings</Tab>
                            <Tab>{LABELS.account.listTitle}</Tab>
                            <Tab>OAuth Applications</Tab>
                        </TabList>
                        <TabPanel>
                            <PanelMultiSettingsBar
                                cacheStatus={cacheStatus}
                                details={{
                                    id: user?.id,
                                }}
                                settings={[
                                    [
                                        {
                                            name: 'Email Address',
                                            summary: user
                                                ? (pendingEmail
                                                    ? <>
                                                        <div>{user.email_address} (current)</div>
                                                        <div>{pendingEmail} (pending) <Tooltip overlay="We've sent a verification email to your new address, please clink the link to verify ownership and confirm the change of email"><Pill>?</Pill></Tooltip></div>
                                                    </>
                                                    : user.email_address)
                                                : <SkeletonBar />,
                                            settingsBtn: user
                                                ? {
                                                    onClick: () => history.push(email.editUri),
                                                    children: 'Change Email Address',
                                                    kind: 'secondary',
                                                }
                                                : null,
                                        },
                                        {
                                            name: 'Email Address Verified',
                                            summary: user
                                                ? (user.email_verified ? "Verified" : "Not Verified")
                                                : <SkeletonBar />,
                                            settingsBtn: !isUserEmailVerified
                                                ? {
                                                    children: 'Resend Verification Email',
                                                    disabled: (isQueryLoading || isVerificationLoading || isUserEmailVerified || !isEmailVerificationRequestable),
                                                    kind: 'secondary',
                                                    onClick: () => handleRequestEmailVerification(),
                                                }
                                                : null,
                                            routeDisabledTooltip: !isEmailVerificationRequestable && !isUserEmailVerified
                                                ? <>
                                                    <p>We've sent you a verification email, please check your email and click the link to verify.</p>
                                                    <p>If you experience any problems verifying, please wait a few minutes and you'll be able to resend the verification email here.</p>
                                                </>
                                                : null,
                                            hint: 'You must verify your email address before you can accept collaborations.',
                                        },
                                        {
                                            name: 'Password',
                                            summary: user ? <HiddenPassword /> : <SkeletonBar />,
                                            settingsBtn: user
                                                ? {
                                                    onClick: () => history.push(passwordEditor.editUri),
                                                    children: 'Change Password',
                                                    kind: 'secondary',
                                                }
                                                : null,
                                        },
                                        {
                                            name: 'Two-Factor Authentication',
                                            summary: user ? (user['2fa'].enabled ? 'Enabled' : 'Not Enabled') : <SkeletonBar />,
                                            settingsBtn: twoFactorButton,
                                            routeDisabledTooltip: user && user['2fa'].required
                                                                ? <>
                                                                    <p>Two-Factor Authentication can't currently be disabled because one or more of <Link to='/user/#accounts'>your accounts</Link> is currently set to require Two-factor Authentication</p>
                                                                    <p><Link to='/user/#accounts'><TooltipButton>My Accounts</TooltipButton></Link></p>
                                                                </>
                                                                : null,
                                        },
                                        {
                                            name: 'Avatar',
                                            summary: user
                                                ? <Avatar
                                                    initial={initial(user)}
                                                    colorId={user.id}
                                                    gravatar_url={user.gravatar_url}
                                                />
                                                : <SkeletonBar size='sm' />,
                                            hint: <span>Change your avatar? visit <a href="https://gravatar.com">gravatar</a><br /> (avatars are linked to your email address)</span>,
                                        },
                                        {
                                            name: 'SSH Public Key',
                                            summary: user ? (user.ssh_key ? user.ssh_key.split('\n').map(sshKeyDetail).map(k => <div key={k}>{k}</div>) : 'None') : <SkeletonBar />,
                                            hint: 'SSH keys are required for passwordless access to Linux-based cloud server images.',
                                            route: sshKey.editUri,
                                        },
                                    ]
                                ]}
                            />
                        </TabPanel>
                        <TabPanel>
                            <AccountList
                                currUserId={currUserId}
                                onLeaveDialogShow={leaveDialog.show}
                                pendingCollaborations={pendingCollaborations}
                                confirmAcceptDialog={confirmAcceptDialog.show}
                                confirmDeclineDialog={confirmDeclineDialog.show}
                                enableTwoFactorDialog={enableTwoFactorDialog.show}
                                requestEmailVerification={requestEmailVerification}
                            />
                        </TabPanel>
                        <TabPanel>
                            <ApplicationList />
                        </TabPanel>
                    </Tabs>
                </Panel>

            }
            editors={[
                { editor: name, render: () => <NameEditPanel editor={name} /> },
                { editor: sshKey, component: UserSshKeyEditor },
                { editor: email, render: () => <UserEmailEditor editor={email} email_address={user?.email_address} pendingEmail={pendingEmail}  /> },
                {
                    editor: {
                        editUri: passwordEditor.editUri,
                        onSave: () => {},
                    },
                    render: () => (
                        <UserPasswordEditor
                            onSubmit={passwordEditor.handleSubmitPasswordChange}
                            onCancel={() => history.goBack()}
                            onMount={passwordEditor.resetEditorState}
                            messages={passwordEditor.messages}
                        />
                    ),
                },
            ]}
        />
    );
};